import { createContext, useContext } from "react";

/**
 * How deep in the dialog stack the surrounding content sits. `-1` means "not
 * inside a dialog at all", which is the default and the value on a plain page.
 *
 * A portal moves DOM but keeps the React tree, so a menu rendered from inside a
 * dialog still reads the dialog's depth from here even though its own DOM hangs
 * off `document.body`. That is the whole point: the menu needs the depth of the
 * thing it belongs to, not of whatever happens to be open when it paints.
 *
 * Deliberately NOT reset by `PortalContextReset`. That reset exists to stop a
 * portaled surface inheriting *layout* adaptations (a Button thinking it is
 * still in a table). Depth is not an adaptation, it is where the surface came
 * from, and severing it would put every popover back at page level.
 */
const DialogDepthContext = createContext<number>(-1);

export const DialogDepthProvider = DialogDepthContext.Provider;

/** Depth of the enclosing dialog, or -1 outside one. */
export function useDialogDepth(): number {
  return useContext(DialogDepthContext);
}
